<script setup lang="ts">
import { onBeforeUnmount, onMounted, onUpdated, ref, watch } from 'vue';
import { domRangesFor, matchRanges, setHighlightRanges, type MatchRange } from '@/utils/highlightMatch';

/**
 * Highlights the part of its content a quick search matched (SAU-467), whatever renders
 * that content: a plain value, a link to a related record, a number.
 *
 * It marks text ranges with the browser's CSS Custom Highlight API, styled by
 * `::highlight(<name>)` in app.css. Nothing in the DOM changes, so the cell renderers
 * inside it need no changes and Vue's own updates are never disturbed. When the content
 * re-renders, the marks are worked out again.
 */
const props = withDefaults(
    defineProps<{
        /** The highlight's name, one per surface, so the list and Cmd+K never clear each other's marks. */
        name: string;
        /** The typed terms to find. Empty, or `active` false, marks nothing. */
        terms: string[];
        active?: boolean;
        /** For a whole-number match: mark the whole text rather than search it. */
        whole?: boolean;
    }>(),
    { active: true, whole: false },
);

const root = ref<HTMLElement | null>(null);
const owner = {};
let observer: MutationObserver | null = null;

function refresh() {
    const element = root.value;
    if (!element || !props.active) {
        setHighlightRanges(props.name, owner, []);
        return;
    }
    const text = element.textContent ?? '';
    const textRanges: MatchRange[] = props.whole ? (text.trim() === '' ? [] : [[0, text.length]]) : matchRanges(text, props.terms);
    setHighlightRanges(props.name, owner, domRangesFor(element, textRanges));
}

/**
 * Only an active highlight watches its content, so a long table pays for the cells of
 * matched fields alone, not for every cell.
 */
function syncObserver() {
    const shouldObserve = props.active && props.terms.length + (props.whole ? 1 : 0) > 0 && root.value !== null;
    if (shouldObserve && !observer && typeof MutationObserver !== 'undefined') {
        observer = new MutationObserver(() => refresh());
        observer.observe(root.value!, { childList: true, subtree: true, characterData: true });
    } else if (!shouldObserve && observer) {
        observer.disconnect();
        observer = null;
    }
}

onMounted(() => {
    syncObserver();
    refresh();
});
onUpdated(refresh);
watch(
    () => [props.terms.join('\u0000'), props.active, props.whole],
    () => {
        syncObserver();
        refresh();
    },
);

onBeforeUnmount(() => {
    observer?.disconnect();
    setHighlightRanges(props.name, owner, []);
});
</script>

<template>
  <span ref="root" class="contents" data-testid="search-highlight"><slot /></span>
</template>
